import { useContext } from 'react';
import useSWR from 'swr';

import { EventWithData } from '../../../../../typings/Event.interface';
import { APISource } from '../../../../../typings/Source.interface';
import { APIWebhook } from '../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../dashboard/client/APIMethodKeys';
import isEmpty from '../../../dashboard/utils/isEmpty';
import { ConsoleGlobalContext } from './ConsoleGlobalContext';

const updated_event_cache = new Map<string, EventWithData>();

const useConsoleEntries = ({ source, webhook }: { source: APISource; webhook: APIWebhook }) => {
  const { HookdeckAPI } = useContext(ConsoleGlobalContext);

  const default_query_filters = !isEmpty(source) && {
    source_id: source.id,
    include: 'data',
    limit: 250,
    cli_id: { all: true },
  };

  const { data: previous_requests } = useSWR(
    default_query_filters && APIMethodKeys.requests.list(default_query_filters),
    () => {
      return HookdeckAPI.requests.list(default_query_filters);
    },
    {
      refreshInterval: 3000,
    },
  );

  const { data: previous_events, mutate: mutateEvents } = useSWR(
    !isEmpty(webhook) && default_query_filters && APIMethodKeys.events.list(default_query_filters),
    () => HookdeckAPI.events.list(default_query_filters),
    {
      refreshInterval: 3000,
    },
  );

  const event_for_request_id = [
    ...Array.from(updated_event_cache.values()),
    ...(previous_events?.models || []),
  ].reduce(
    (acc, event) => {
      if (!acc[event.request_id]) {
        acc[event.request_id] = event;
      } else if (
        new Date(event.updated_at).getTime() > new Date(acc[event.request_id].updated_at).getTime()
      ) {
        acc[event.request_id] = event;
      }
      return acc;
    },
    {} as { [key: string]: EventWithData },
  );

  const merged_entries = previous_requests?.models.map((request) => {
    if (event_for_request_id[request.id]) {
      return {
        ...event_for_request_id[request.id],
        created_at: request.created_at,
      };
    }
    return request;
  });

  const is_loading =
    previous_requests === undefined || (!isEmpty(webhook) && previous_events === undefined);

  return {
    entries: !is_loading ? merged_entries?.slice(0, 250) : undefined,
    is_loading: is_loading,
    addEvent: (event: EventWithData): void => {
      updated_event_cache.set(event.id, event);
      mutateEvents(
        (state) => {
          if (!state) {
            return {
              pagination: {},
              count: 1,
              models: [event],
            };
          }
          return { ...state, models: [...state.models, event] };
        },
        {
          revalidate: false,
        },
      );
    },
    mutateEvent: (event: EventWithData): void => {
      updated_event_cache.set(event.id, event);
      mutateEvents(
        (state) => {
          if (!state) {
            return {
              pagination: {},
              count: 1,
              models: [event],
            };
          }
          return {
            ...state,
            models: state.models.map((model) =>
              model.id === event.id ? { ...event, data: model.data } : model,
            ),
          };
        },
        {
          revalidate: false,
        },
      );
    },
  };
};

export default useConsoleEntries;
